import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import { configurationPageContentClass } from '@modules/configuration/components/configurationLayout';
import { useCallback, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { usePrinters, usePrintersSettings } from '../../hooks';
import { mapFilterConditionsToPrinterFilters } from '../../models/Filters';
import { CardList, Table } from '../display';
import { ErrorState } from '../feedback';
import { FiltersSection } from '../interaction';
import { PrintersListSkeleton, PrintersSkeleton } from '../skeletons';
import { EditPrinterModal, EditPrinterModalRef } from '../modals/EditPrinterModal';

export const Main = () => {
  const { t } = useTranslation('printersModule');

  const [searchTerm, setSearchTerm] = useState('');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);

  const { isLoading: settingsLoading } = usePrintersSettings();

  const processedFilters = useMemo(
    () => mapFilterConditionsToPrinterFilters(filterConditions, null),
    [filterConditions],
  );

  const { printers, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } = usePrinters({
    searchTerm,
    ...processedFilters,
    enabled: true,
  });

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleFilterChange = useCallback((conditions: FilterConditionOutput[]) => {
    setFilterConditions(conditions);
  }, []);

  const printersViewProps = useMemo(
    () => ({
      printers,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      onRefresh: refetch,
    }),
    [printers, fetchNextPage, hasNextPage, isFetchingNextPage, total, refetch],
  );

  const editModalRef = useRef<EditPrinterModalRef>(null);

  const handleEdit = useCallback((printer: any) => {
    editModalRef.current?.open(printer);
  }, []);

  if (settingsLoading) {
    return <PrintersSkeleton />;
  }

  const renderPrinters = () => {
    if (isMobile) {
      return <CardList {...printersViewProps} showActions onEdit={handleEdit} />;
    }
    return <Table {...printersViewProps} onEdit={handleEdit} />;
  };

  return (
    <div className="w-full pb-6" data-testid="printers-view">
      <ConfigurationPageHeader title={t('pageTitle')} description={t('pageDescription')} />

      <div className={configurationPageContentClass}>
        <div className="mb-6">
          <FiltersSection
            handleSearch={handleSearch}
            onFilterChange={handleFilterChange}
            filterConditions={filterConditions}
          />
        </div>

        {isLoading && <PrintersListSkeleton />}

        {error && (
          <div className="mb-4">
            <ErrorState error={error} onRetry={refetch} title={t('error.load_printers')} className="h-64" />
          </div>
        )}

        {!isLoading && !error && printers.length > 0 && renderPrinters()}

        {!isLoading && !error && printers.length === 0 && (
          <EmptyView type={searchTerm ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.DEFAULT} />
        )}
      </div>

      <EditPrinterModal ref={editModalRef} onUpdated={refetch} />
    </div>
  );
};
